import React, { useState, useEffect } from "react";
import axios from "axios";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ExportIcon from "../../components/Images/icons/Export_Icon_01.png";
import { toast } from "react-toastify";
import Response_Loading_processing from "../../layout/Others/Response_Loading_processing";
import { getItemToken } from "../../Utils/localStorageUtils";
import { donwloadCSV, videodownloadPDF, videodownloadXLSX } from "../../components/CommonMethod/CommonMethods";
import { MdKeyboardDoubleArrowLeft, MdKeyboardDoubleArrowRight } from "react-icons/md";
import { RxReload } from "react-icons/rx";
import { useMyContext } from "../../components/Provider/CommonProvider";

function Partner_analytis_payout() {
  const [apiData, setApiData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [withOutRefresh, setWithoutRefresh] = useState(false);
  
  // Get default date range (last 7 days)
  const getDefaultDateRange = () => {
    const today = new Date();
    const oneWeekAgo = new Date(today);
    oneWeekAgo.setDate(today.getDate() - 7);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      start: formatDate(oneWeekAgo),
      end: formatDate(today)
    };
  };
  
  // Date filter states - default to last 7 days
  const defaultDates = getDefaultDateRange();
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);
  
  const { fetchRequest } = useMyContext();
  const BaseUrl = process.env.REACT_APP_Baseurl;

  const fetchData = async () => {
    setLoading(true);
    try {
      const listApi = `${BaseUrl}/admin/payout-analytics?start_date=${startDate}&end_date=${endDate}`;
      const response = await fetchRequest({
        method: "GET",
        url: listApi,
        data: {},
      });
      
      if (response?.status) {
        setApiData(response?.data || []);
        setFilteredData(response?.data || []);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(error?.response?.data?.message || "Failed to fetch data");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [withOutRefresh, startDate, endDate]);

  const handleFilterChange = (event) => {
    const searchTerm = event.target.value.toLowerCase();
    setFilter(searchTerm);
    
    const filteredResults = apiData.filter((item) => {
      return (
        (item?.partner_id?.toString()?.toLowerCase()?.includes(searchTerm)) ||
        (item?.user?.name?.toLowerCase()?.includes(searchTerm)) ||
        (item?.total_views?.toString()?.toLowerCase()?.includes(searchTerm)) ||
        (item?.total_monetization?.toString()?.toLowerCase()?.includes(searchTerm))
      );
    });
    setFilteredData(filteredResults);
  };

  const handleClear = () => {
    setFilter("");
    setFilteredData(apiData);
  };

  const reloadAction = () => {
    setWithoutRefresh(prev => !prev);
  };

  const handleDateFilter = () => {
    setWithoutRefresh(prev => !prev);
  };

  // Helper to get content type breakdown text
  const getContentBreakdown = (breakdown) => {
    if (!breakdown) return 'N/A';
    const types = Object.keys(breakdown);
    return types.map(type => {
      const typeMap = { '1': 'Video', '2': 'Series', '3': 'Live', '4': 'Audio' };
      return `${typeMap[type] || 'Unknown'}: ${breakdown[type].views} views`;
    }).join(', ');
  };

  // Export Functions
  const fetchAllData = async (type, find) => {
    let updatedList = find === "ALL" ? apiData : filteredData;

    const exportData = updatedList.map((item, index) => ({
      "S.NO": index + 1,
      "ID": item?.partner_id,
      "CHANNEL NAME": item?.user?.name || 'N/A',
      "TOTAL VIEWS": item?.total_views,
      "MONETIZED AMOUNT": item?.total_monetization,
    }));

    if (type === "CSV") {
      await donwloadCSV(exportData, "PARTNER-MONETIZATION-ANALYTICS");
    } else if (type === "PDF") {
      await videodownloadPDF(exportData, "PARTNER-MONETIZATION-ANALYTICS", [
        "S.NO", "ID", "CHANNEL NAME", "TOTAL VIEWS", "MONETIZED AMOUNT"
      ]);
    } else if (type === "XLSX") {
      await videodownloadXLSX(exportData, "PARTNER-MONETIZATION-ANALYTICS", [
        "S.NO", "ID", "CHANNEL NAME", "TOTAL VIEWS", "MONETIZED AMOUNT"
      ]);
    }
  };

  // Table Columns - Matching your UI with proper widths
  const columns = [
    {
      dataField: "rowNumber",
      text: "Id",
      sort: false,
      headerStyle: { width: '70px', minWidth: '70px' },
      style: { width: '70px', minWidth: '70px' },
      formatter: (cell, row, rowIndex) => rowIndex + 1
    },
    {
      dataField: "user.name",
      text: "Channel Name",
      sort: true,
      formatter: (cell, row) => row?.user?.name || 'N/A',
      headerStyle: { width: '150px', minWidth: '150px' },
      style: { width: '150px', minWidth: '150px' }
    },
    {
      dataField: "content_breakdown",
      text: "Content Title",
      sort: false,
      formatter: (cell, row) => {
        if (!cell) return 'No content';
        const types = Object.keys(cell);
        const typeMap = { '1': 'Video', '2': 'Series', '3': 'Live', '4': 'Audio' };
        return types.map(type => `${typeMap[type] || 'Type ' + type}`).join(', ');
      },
      headerStyle: { width: '150px', minWidth: '150px' },
      style: { width: '150px', minWidth: '150px' }
    },
    {
      dataField: "content_breakdown",
      text: "Content Type",
      sort: false,
      formatter: (cell) => {
        if (!cell) return 'N/A';
        const types = Object.keys(cell);
        const typeMap = { '1': 'Video', '2': 'Series', '3': 'Live', '4': 'Audio' };
        return types.map(type => typeMap[type] || 'Unknown').join(', ');
      },
      headerStyle: { width: '150px', minWidth: '150px' },
      style: { width: '150px', minWidth: '150px' }
    },
    {
      dataField: "total_views",
      text: "Views",
      sort: true,
      headerStyle: { width: '100px', minWidth: '100px' },
      style: { width: '100px', minWidth: '100px' }
    },
    {
      dataField: "total_monetization",
      text: "Monetized Amount",
      sort: true,
      formatter: (cell) => cell ? `$${parseFloat(cell).toFixed(2)}` : '$0.00',
      headerStyle: { width: '160px', minWidth: '160px' },
      style: { width: '160px', minWidth: '160px' }
    },
    {
      dataField: "admin_commission",
      text: "Admin Commission",
      sort: true,
      formatter: (cell) => cell ? `$${parseFloat(cell).toFixed(2)}` : '$0.00',
      headerStyle: { width: '180px', minWidth: '180px' },
      style: { width: '180px', minWidth: '180px' }
    },
    {
      dataField: "partner_commission",
      text: "Partner Commission",
      sort: true,
      formatter: (cell) => cell ? `$${parseFloat(cell).toFixed(2)}` : '$0.00',
      headerStyle: { width: '180px', minWidth: '180px' },
      style: { width: '180px', minWidth: '180px' }
    },
  ];

  // Pagination Config
  const paginationConfig = paginationFactory({
    page: 1,
    sizePerPage: 10,
    nextPageText: "Next",
    prePageText: "Prev",
    firstPageText: <MdKeyboardDoubleArrowLeft />,
    lastPageText: <MdKeyboardDoubleArrowRight />,
    showTotal: true,
    alwaysShowAllBtns: true,
    sizePerPageList: [
      { text: "5", value: 5 },
      { text: "10", value: 10 },
      { text: "All", value: filteredData?.length },
    ],
  });

  return (
    <>
      <section className="section container-fluid pt-3">
        <div className="row">
          <div className="col-sm-12">
            <div className="iq-card">
              <div className="iq-card-header d-flex justify-content-between">
                <div className="iq-header-title">
                  <h4 className="card-title">Partner Analytics & Payout</h4>
                </div>
              </div>
              <hr />

              <div className="iq-card-body">
                {/* Date Filter Section */}
                <div className="row mb-3 align-items-end">
                  <div className="col-md-4">
                    <label className="form-label">Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <button 
                      className="btn btn-primary"
                      onClick={handleDateFilter}
                    >
                      Apply Filter
                    </button>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center flex-wrap mb-3 gap-3">
                  <div className="filterContainer">
                    <input
                      type="text"
                      placeholder="Filter all.."
                      className="filter form-control"
                      value={filter}
                      onChange={handleFilterChange}
                    />
                    {filter?.length > 0 && (
                      <button type="button" className="closeBtn" onClick={handleClear}>
                        <i className="fa fa-times"></i>
                      </button>
                    )}
                  </div>

                  <div className="btn-group flex-wrap justify-content-between justify-content-sm-end">
                    <button
                      className="bg-transparent reloadButton theme-text-color p-2 me-2 border border-2 rounded-circle theme-border-color"
                      onClick={reloadAction}
                    >
                      <RxReload style={{ width: "18px", height: "15px" }} className={`${loading ? "rotate-load" : ""}`} />
                    </button>

                    <div className="dropdown d-block mb-2 mb-sm-0 mx-0" key={"2"}>
                      <button
                        className="btn btn-success dropdown-toggle d-flex mx-0"
                        type="button"
                        id="dropdownMenuButton1"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <span className="fw-bolder">Export </span>
                        <img src={ExportIcon} className="ms-2 exportIcon" alt="exportIcon" />
                      </button>
                      <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                        <li className="dropdown-item" onClick={() => fetchAllData("CSV")}>
                          CSV
                        </li>
                        <li className="dropdown-item" onClick={() => fetchAllData("PDF")}>
                          PDF
                        </li>
                        <li className="dropdown-item" onClick={() => fetchAllData("XLSX")}>
                          XLSX
                        </li>
                      </ul>
                    </div>

                    <div className="dropdown d-block">
                      <button
                        className="btn btn-success dropdown-toggle d-flex"
                        type="button"
                        id="dropdownMenuButton2"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <span className="fw-bolder">Export All</span>
                        <img src={ExportIcon} className="ms-2 exportIcon" alt="exportAllIcon" />
                      </button>
                      <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton2">
                        <li className="dropdown-item" onClick={() => fetchAllData("CSV", "ALL")}>
                          CSV
                        </li>
                        <li className="dropdown-item" onClick={() => fetchAllData("PDF", "ALL")}>
                          PDF
                        </li>
                        <li className="dropdown-item" onClick={() => fetchAllData("XLSX", "ALL")}>
                          XLSX
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="table-responsive" style={{ overflowX: 'auto', width: '100%' }}>
                  <div className="bootstrapTable" style={{ minWidth: '1200px' }}>
                    <BootstrapTable
                      keyField="id"
                      columns={columns}
                      data={filteredData}
                      pagination={paginationConfig}
                      className="no-border-table"
                      noDataIndication={
                        <div className="noRecord">
                          {loading ? (
                            <label>
                              <Response_Loading_processing />
                              Loading...
                            </label>
                          ) : (
                            filteredData?.length <= 0 && <p>No Data Found!</p>
                          )}
                        </div>
                      }
                      hover
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Partner_analytis_payout;
